import React, { useState, useEffect, useCallback } from "react";
import { Handshake, Plus, Download, CheckCircle2, XCircle, Loader2, Wallet, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { authApi } from "./authApi";
import { API } from "./api";

const inp = "w-full bg-white/5 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B] transition-colors";
const eur = (n) => { const v = parseFloat(n); return Number.isFinite(v) && v > 0 ? `${Math.round(v).toLocaleString("fr-FR")} €` : "—"; };

const STATUS_STYLE = {
  active: "text-sky-300 border-sky-400/40 bg-sky-500/10",
  sold: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10",
  cancelled: "text-zinc-400 border-white/15 bg-white/5",
};
const STATUS_LABEL = { active: "En cours", sold: "Vendu", cancelled: "Annulé" };

function CreateForm({ partners, onCreated, onCancel }) {
  const [listings, setListings] = useState([]);
  const [form, setForm] = useState({ listing_slug: "", partner_id: "", owner_pct: 75, partner_pct: 25, commission_base: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { authApi.myListings().then((l) => setListings(l || [])).catch(() => setListings([])); }, []);

  const setOwner = (v) => { const op = Math.max(0, Math.min(100, parseInt(v || 0))); setForm((f) => ({ ...f, owner_pct: op, partner_pct: 100 - op })); };
  const submit = async () => {
    setErr("");
    if (!form.listing_slug || !form.partner_id) { setErr("Sélectionnez une annonce et un partenaire."); return; }
    setBusy(true);
    try { await authApi.netCreateComandat(form); onCreated(); }
    catch (e) { setErr(e.message || "Création impossible."); }
    setBusy(false);
  };

  return (
    <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/5 p-4 space-y-3" data-testid="comandat-form">
      <p className="text-[11px] uppercase tracking-wider text-[#C19A6B]">Nouveau co-mandat</p>
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Mon annonce</label>
        <select value={form.listing_slug} onChange={(e) => setForm((f) => ({ ...f, listing_slug: e.target.value }))} className={inp} data-testid="comandat-listing">
          <option value="" className="bg-[#0d0d10]">— Choisir une annonce —</option>
          {listings.map((l) => <option key={l.slug} value={l.slug} className="bg-[#0d0d10]">{l.title} · {l.city}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Co-commercialisateur (partenaire)</label>
        <select value={form.partner_id} onChange={(e) => setForm((f) => ({ ...f, partner_id: e.target.value }))} className={inp} data-testid="comandat-partner">
          <option value="" className="bg-[#0d0d10]">— Choisir un partenaire —</option>
          {partners.map((p) => <option key={p.member.member_id} value={p.member.member_id} className="bg-[#0d0d10]">{p.member.display_name}{p.member.company ? ` · ${p.member.company}` : ""}</option>)}
        </select>
        {partners.length === 0 && <p className="text-[11px] text-amber-400/80 mt-1">Aucun partenaire accepté. Ajoutez d'abord des partenaires dans « Mes partenaires ».</p>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Ma part %</label>
          <input type="number" min="0" max="100" value={form.owner_pct} onChange={(e) => setOwner(e.target.value)} className={inp} data-testid="comandat-owner-pct" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Sa part %</label>
          <input type="number" value={form.partner_pct} readOnly className={`${inp} opacity-70`} data-testid="comandat-partner-pct" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Honoraires € <span className="text-zinc-600">(opt.)</span></label>
          <input type="number" value={form.commission_base} onChange={(e) => setForm((f) => ({ ...f, commission_base: e.target.value }))} placeholder="ex : 18000" className={inp} data-testid="comandat-commission" />
        </div>
      </div>
      {err && <p className="text-xs text-red-400" data-testid="comandat-error">{err}</p>}
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy} data-testid="comandat-submit" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm uppercase tracking-wider px-4 py-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Créer le co-mandat</button>
        <button onClick={onCancel} className="border border-white/15 text-zinc-400 text-sm uppercase tracking-wider px-4 py-2 hover:text-white">Annuler</button>
      </div>
    </div>
  );
}

function ComandatRow({ c, onChange }) {
  const [busy, setBusy] = useState(false);
  const [selling, setSelling] = useState(false);
  const [sale, setSale] = useState({ commission_base: c.commission_base || "", sale_price: c.sale_price || "" });
  const isOwner = c.role === "owner";
  const other = isOwner ? c.partner : c.owner;

  const doSold = async () => {
    setBusy(true);
    try { await authApi.netMarkComandatSold(c.id, { commission_base: sale.commission_base, sale_price: sale.sale_price }); setSelling(false); onChange(); } catch (_) {}
    setBusy(false);
  };
  const doPayout = async () => {
    setBusy(true);
    try { await authApi.netUpdateComandat(c.id, { payout_status: c.payout_status === "paid" ? "pending" : "paid" }); onChange(); } catch (_) {}
    setBusy(false);
  };
  const doCancel = async () => {
    if (!window.confirm("Annuler ce co-mandat ?")) return;
    setBusy(true);
    try { await authApi.netCancelComandat(c.id); onChange(); } catch (_) {}
    setBusy(false);
  };

  return (
    <div className="border border-white/10 p-3 space-y-2" data-testid={`comandat-${c.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${STATUS_STYLE[c.status]}`}>{STATUS_LABEL[c.status] || c.status}</span>
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
          {isOwner ? <><ArrowUpRight className="w-3 h-3" /> Je verse</> : <><ArrowDownLeft className="w-3 h-3" /> Je perçois</>}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">{c.listing_title}</div>
          <div className="text-xs text-zinc-500 truncate">{isOwner ? "Co-commercialisé par" : "Titulaire :"} {other?.display_name || "—"} · partage {c.owner_pct}/{c.partner_pct}</div>
        </div>
        <div className="text-right">
          <div className="text-sm text-[#C19A6B] font-bold">{eur(c.retro_amount)}</div>
          <div className="text-[10px] text-zinc-500">rétrocommission {c.payout_status === "paid" ? "· réglée" : ""}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <a href={`${API}/network/comandats/${c.id}/statement.pdf`} target="_blank" rel="noopener noreferrer" data-testid={`comandat-statement-${c.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-3 py-1.5"><Download className="w-3.5 h-3.5" /> Relevé PDF</a>
        {isOwner && c.status === "active" && !selling && (
          <button onClick={() => setSelling(true)} disabled={busy} data-testid={`comandat-sell-${c.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/25 px-3 py-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Marquer vendu</button>
        )}
        {isOwner && c.status === "sold" && (
          <button onClick={doPayout} disabled={busy} data-testid={`comandat-payout-${c.id}`} className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-3 py-1.5 border ${c.payout_status === "paid" ? "border-white/15 text-zinc-400" : "border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10"}`}><Wallet className="w-3.5 h-3.5" /> {c.payout_status === "paid" ? "Marquer non réglé" : "Marquer réglé"}</button>
        )}
        {isOwner && c.status !== "cancelled" && (
          <button onClick={doCancel} disabled={busy} data-testid={`comandat-cancel-${c.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider border border-white/15 text-zinc-500 hover:text-red-400 hover:border-red-400/40 px-3 py-1.5"><XCircle className="w-3.5 h-3.5" /> Annuler</button>
        )}
      </div>

      {selling && (
        <div className="border border-emerald-400/20 bg-emerald-500/5 p-3 space-y-2" data-testid={`comandat-sell-form-${c.id}`}>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" value={sale.sale_price} onChange={(e) => setSale((s) => ({ ...s, sale_price: e.target.value }))} placeholder="Prix de vente €" className={inp} data-testid={`comandat-sale-price-${c.id}`} />
            <input type="number" value={sale.commission_base} onChange={(e) => setSale((s) => ({ ...s, commission_base: e.target.value }))} placeholder="Honoraires d'agence €" className={inp} data-testid={`comandat-sale-commission-${c.id}`} />
          </div>
          <p className="text-[11px] text-zinc-500">Rétrocommission estimée : <span className="text-[#C19A6B]">{eur((parseFloat(sale.commission_base || 0) * c.partner_pct) / 100)}</span></p>
          <div className="flex gap-2">
            <button onClick={doSold} disabled={busy} className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-xs uppercase tracking-wider px-4 py-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer la vente"}</button>
            <button onClick={() => setSelling(false)} className="border border-white/15 text-zinc-400 text-xs uppercase tracking-wider px-4 py-2">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ComandatSection({ partners = [], hideTitle = false }) {
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => { authApi.netComandats().then(setData).catch(() => setData({ comandats: [], to_receive: 0, to_pay: 0 })); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const list = data.comandats || [];

  return (
    <div className="border border-white/10 p-5 space-y-4" data-testid="comandat-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {!hideTitle && <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Handshake className="w-5 h-5 text-[#C19A6B]" /> Co-mandats & rétrocommissions</p>}
        {!creating && <button onClick={() => setCreating(true)} data-testid="comandat-new" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-3 py-1.5"><Plus className="w-3.5 h-3.5" /> Nouveau co-mandat</button>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="border border-white/10 p-4"><ArrowDownLeft className="w-4 h-4 text-emerald-300 mb-2" /><div className="text-2xl font-bold text-white" data-testid="comandat-to-receive">{eur(data.to_receive)}</div><div className="text-[11px] text-zinc-500 mt-1">Rétrocommissions à percevoir</div></div>
        <div className="border border-white/10 p-4"><ArrowUpRight className="w-4 h-4 text-[#C19A6B] mb-2" /><div className="text-2xl font-bold text-white" data-testid="comandat-to-pay">{eur(data.to_pay)}</div><div className="text-[11px] text-zinc-500 mt-1">Rétrocommissions à verser</div></div>
      </div>

      {creating && <CreateForm partners={partners} onCreated={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />}

      {list.length === 0 ? (
        <p className="text-xs text-zinc-500" data-testid="comandat-empty">Aucun co-mandat pour le moment. Créez-en un avec un partenaire, ou marquez un apport « Vendu » pour en générer un automatiquement.</p>
      ) : (
        <div className="space-y-2" data-testid="comandat-list">
          {list.map((c) => <ComandatRow key={c.id} c={c} onChange={load} />)}
        </div>
      )}
    </div>
  );
}
